import React from "react";
import { Sparkles, X, Lock } from "lucide-react";
import { useAudioAccess } from "../AudioAccessContext";

/**
 * Modal shown when a guest (unauthenticated/unsubscribed) clicks Play on the
 * Audio Comics catalogue.  All copy and pricing is driven by the platform
 * settings served by /api/audio/access-mode (Admin → Settings).
 */
export default function PlayPaywallModal({ open, onClose, onSignIn, onSubscribe }) {
  const access = useAudioAccess();
  if (!open) return null;
  const subscribeLabel = access.audio_mode === "PAID"
    ? `${access.cta_secondary} · ${access.currency_symbol}${access.subscription_price}/mo`
    : access.cta_secondary;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="play-paywall-modal"
    >
      <div
        className="relative max-w-md w-full rounded-2xl bg-[#16101f] border border-white/10 p-7 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-white/50 hover:text-white"
          aria-label="Close"
          data-testid="play-paywall-close"
        >
          <X size={18} />
        </button>

        <div className="w-12 h-12 mb-5 rounded-xl bg-gradient-to-br from-fuchsia-500 to-amber-400 grid place-items-center">
          <Lock size={20} strokeWidth={2.2} />
        </div>

        <h2 className="text-xl sm:text-2xl font-semibold leading-tight">
          Subscribe to Listen
        </h2>
        <p className="mt-3 text-sm text-white/65 leading-relaxed">
          Browsing the Audio Library is completely <span className="text-emerald-300 font-medium">FREE</span>.
          A subscription is required only to listen to the complete Bala Basava
          Audio Comics.
        </p>

        <div className="mt-6 grid gap-3">
          <button
            onClick={onSignIn}
            className="w-full inline-flex items-center justify-center gap-3 px-5 py-3 rounded-full bg-white text-black font-semibold hover:bg-white/90"
            data-testid="play-paywall-google"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Google Sign In
          </button>
          <button
            onClick={onSubscribe}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-amber-400 text-black font-semibold hover:bg-amber-300"
            data-testid="play-paywall-subscribe"
          >
            <Sparkles size={14} /> {subscribeLabel}
          </button>
        </div>

        <p className="mt-5 text-[11px] text-center text-white/40">
          Unlimited access · cancel anytime
        </p>
      </div>
    </div>
  );
}
